import React, { useCallback, useMemo, useRef } from "react";

import classnames from "classnames";
import PropTypes from "prop-types";
import MaskedInput from "react-text-mask";

import Join, { JoinItem } from "Cobalt/Join";
import type { InputProps } from "Theme/Input";

import makeBem from "Utils/makeBem";

const bem = makeBem("CobaltInput");
export const Input = ({ className, prependContent, appendContent, theme = "default", meta, input, mask = false, errorRight, ...rest }: InputProps) => {
  const maskedInputRef = useRef<MaskedInput | null | undefined>(null);
  const hasWrapper = prependContent || appendContent || errorRight;
  const hasError = !!(meta && meta.touched && meta.error);
  const classes = useMemo(
    () =>
      classnames(bem(), !hasWrapper && className, {
        [bem(null, "prepended")]: !!prependContent,
        [bem(null, "appended")]: !!appendContent,
        [bem(null, `theme-${theme}`)]: theme !== "default",
        [bem(null, "error")]: hasError,
      }),
    [className, prependContent, appendContent, theme, hasError],
  );
  const wrapperClasses = useMemo(() => classnames(bem("Wrap"), className || ""), [className]);
  const contentClasses = useCallback(
    (modifier: "prepend" | "append") =>
      classnames(bem("Span", modifier), {
        [bem("Span", `theme-${theme}`)]: theme !== "default",
      }),
    [theme],
  );
  const inputElement = <MaskedInput ref={maskedInputRef} className={classes} aria-invalid={hasError} mask={mask} {...input} {...rest} />;
  return hasWrapper ? (
    <Join className={wrapperClasses}>
      {prependContent && (
        <JoinItem>
          <span className={contentClasses("prepend")}>{prependContent}</span>
        </JoinItem>
      )}
      <JoinItem>{inputElement}</JoinItem>
      {appendContent && (
        <JoinItem>
          <span className={contentClasses("append")}>{appendContent}</span>
        </JoinItem>
      )}
    </Join>
  ) : (
    inputElement
  );
};
Input.propTypes = {
  className: PropTypes.string,
  prependContent: PropTypes.string,
  appendContent: PropTypes.string,
  theme: PropTypes.oneOf(["default", "condensed"]),
  meta: PropTypes.shape({
    touched: PropTypes.bool,
    error: PropTypes.any,
    warning: PropTypes.any,
  }),
  input: PropTypes.shape({
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  }),
  mask: PropTypes.oneOfType([PropTypes.bool, PropTypes.func]),
  showMask: PropTypes.bool,
  guide: PropTypes.bool,
  errorRight: PropTypes.bool,
};
